import { Box, Container, Grid, Stack, Typography } from '@mui/material';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import {
  AutocompleteElement,
  FormContainer,
  PasswordElement,
  TextFieldElement,
} from 'react-hook-form-mui';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import Link from '../../components/Link';
import { routes } from '../../config/routes';
import { useSignUpForm } from '../../components/pages/auth/signup/useSignUpForm';
import { NextHead } from '../../components/NextHead';
import { useCountries } from '../../services/hooks/useCountries';
import { WaveLayout } from '../../components/WaveLayout';

import { TermsAcceptanceText } from '../../components/TermsAcceptanceText';
import { CaCredentialsNotice } from '../../components/CaCredentialsNotice';
import { PoweredByCa } from '../../components/PoweredByCa';
import { getStaticPropsWithTranslations } from '../../utils/translation';
import { FormInitializer } from '../../components/FormInitializer';
import { useState } from 'react';
import { CaftaTurnstileField } from '../../components/CaftaTurnstile/CaftaTurnstileField';

const nameSpaces = ['common', 'auth'];

export function SignUp() {
  const { query } = useRouter();
  const { email } = query;

  const schema = yup
    .object({
      mail: yup.string().email().required(),
      firstName: yup.string().required(),
      lastName: yup.string().required(),
      pass: yup.string().required(),
      country: yup
        .object({
          id: yup.string().required(),
          label: yup.string().required(),
        })
        .required(),
      gender: yup
        .object({
          id: yup.string().required(),
          label: yup.string().required(),
        })
        .required(),
    })
    .required();
  const { t } = useTranslation(nameSpaces);
  const { onSuccess, loading } = useSignUpForm();
  const { data } = useCountries();

  const [turnstileToken, setTurnstileToken] = useState<string>();

  const countriesList = data?.data
    ? data?.data.map((c) => ({ label: c.name, id: c.id }))
    : [{ label: 'Loading...', id: '-1' }];

  const genders = [
    { id: 'm', label: t('auth:signUp:fields:genders:m') },
    { id: 'f', label: t('auth:signUp:fields:genders:f') },
    { id: 'x', label: t('auth:signUp:fields:genders:x') },
  ];

  return (
    <>
      <NextHead pageName={t('auth:signUp:title')} />
      <WaveLayout>
        <Container>
          <Box
            height="100%"
            flex={1}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            sx={{ overflowY: 'auto' }}
          >
            <Typography variant="h4" textAlign="center" color="primary" pt={4}>
              {t('auth:signUp:callToAction')}
            </Typography>
            <Stack spacing={2} maxWidth={700} py={4}>
              <Card sx={{ minWidth: 275 }}>
                <CardContent>
                  <Box margin={2}>
                    <CaCredentialsNotice />
                    <FormContainer
                      onSuccess={onSuccess}
                      resolver={yupResolver(schema)}
                    >
                      <FormInitializer initialValues={{ mail: email }} />
                      <Grid container spacing={2} mt={2}>
                        <Grid item xs={12}>
                          <TextFieldElement
                            type="email"
                            name="mail"
                            label={t('auth:signUp:fields:email')}
                            variant="standard"
                            color="secondary"
                            fullWidth
                            required
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextFieldElement
                            type="text"
                            name="firstName"
                            label={t('auth:signUp:fields:firstName')}
                            variant="standard"
                            color="secondary"
                            fullWidth
                            required
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextFieldElement
                            type="text"
                            name="lastName"
                            label={t('auth:signUp:fields:lastName')}
                            variant="standard"
                            color="secondary"
                            fullWidth
                            required
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <PasswordElement
                            name="pass"
                            label={t('auth:signUp:fields:password')}
                            variant="standard"
                            color="secondary"
                            fullWidth
                            required
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <AutocompleteElement
                            label={t('auth:signUp:fields:country')}
                            name="country"
                            textFieldProps={{
                              variant: 'standard',
                              fullWidth: true,
                              color: 'secondary',
                            }}
                            required
                            options={countriesList}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <AutocompleteElement
                            label={t('auth:signUp:fields:gender')}
                            name="gender"
                            textFieldProps={{
                              variant: 'standard',
                              fullWidth: true,
                              color: 'secondary',
                            }}
                            required
                            options={genders}
                          />
                        </Grid>
                        <Grid container item xs={12} justifyContent="center">
                          <CaftaTurnstileField onVerify={setTurnstileToken} />
                        </Grid>
                        <Grid container item xs={12} justifyContent="center">
                          <LoadingButton
                            type="submit"
                            variant="contained"
                            loading={loading}
                            disabled={!turnstileToken}
                            sx={{ minWidth: '200px' }}
                          >
                            {t('auth:signUp:action')}
                          </LoadingButton>
                        </Grid>
                      </Grid>
                      <Stack mt={4} spacing={2} alignItems="center">
                        <Typography variant="subtitle1" textAlign="center">
                          <Link color="secondary" href={routes.FORGOT_PASSWORD}>
                            {t('auth:forgotPasswordQuestion')}
                          </Link>{' '}
                          - {t('auth:signUp:hasAccount')}{' '}
                          <Link href={routes.SIGN_IN()} color="secondary">
                            {t('auth:hasAccountAction')}
                          </Link>
                        </Typography>
                      </Stack>
                    </FormContainer>
                  </Box>
                </CardContent>
              </Card>
              <TermsAcceptanceText />
              <PoweredByCa />
            </Stack>
          </Box>
        </Container>
      </WaveLayout>
    </>
  );
}

export const getStaticProps = getStaticPropsWithTranslations(nameSpaces);

export default SignUp;
